// Shared LLM-key status rules for every key matrix (Platform company keys, a
// partner's own keys, a partner's client-company keys). Kept out of the
// components so the effective-key logic can't drift between screens.
//
// Two providers are special (see the llm-provider-roles notes):
//   - Cohere is an optional KB search re-ranker, not a chat model — "no key" is
//     not an error, search just runs unranked, so its badges read softer.
//   - OpenAI also powers knowledge-base uploads/search (embeddings).

import { KEYS_COPY } from '../../../constants/partnerCopy';

export const providerSlug = (provider) => String(provider?.name || '').toLowerCase();

/** Provider-specific helper copy shown under the provider name. */
export const providerHelperText = (slug) => {
  if (slug === 'cohere') {
    return 'Search re-ranking (optional) — improves knowledge-base search quality. Not a chat model.';
  }
  if (slug === 'openai') {
    return 'Also powers knowledge-base features: file uploads and document search are billed to this key. Turning off OpenAI disables KB uploads and KB-enabled agents for this company.';
  }
  return null;
};

/** Credential lists come back as an array or `{ credentials }` depending on the endpoint. */
export const normalizeCredentials = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.credentials)) return data.credentials;
  if (Array.isArray(data?.llm_credentials)) return data.llm_credentials;
  return [];
};

/**
 * One matrix row per provider, merged with the credential rows returned.
 * A provider with no row is a real state: for a partner's client company it
 * means "inherits the partner's key" (the default).
 */
export const buildKeyRows = (providers = [], credentials = []) => {
  const credByProvider = new Map((credentials || []).map((c) => [c.provider_id, c]));
  return (providers || []).map((provider) => {
    const cred = credByProvider.get(provider.id) || null;
    const slug = providerSlug(provider);
    return {
      providerId: provider.id,
      name: provider.display_name || provider.name,
      slug,
      isCohere: slug === 'cohere',
      helperText: providerHelperText(slug),
      cred,
      hasRow: !!cred,
      hasKey: !!cred?.has_key,
      keyLast4: cred?.key_last4 || null,
      allowFallback: !!cred?.allow_platform_fallback,
      // No row ⇒ inherits (backend default).
      inheritFromPartner: cred ? cred.inherit_from_partner !== false : true,
    };
  });
};

/** Platform-direct company: own key → platform fallback → blocked. */
export const resolvePlatformDirect = (row) => {
  if (row?.isCohere) {
    if (row.hasKey) return { label: 'Re-ranking: company key', tone: 'success' };
    if (row.allowFallback) return { label: 'Re-ranking: platform', tone: 'info' };
    return { label: 'Re-ranking off', tone: 'neutral' };
  }
  if (row?.hasKey) return { label: 'Company key', tone: 'success' };
  if (row?.allowFallback) return { label: 'Platform key', tone: 'info' };
  return { label: 'Blocked', tone: 'blocked' };
};

/** A partner's own keys (its home company): own key → platform fallback → blocked. */
export const resolvePartnerOwn = (row) => {
  if (row?.isCohere) {
    if (row.hasKey) return { label: 'Re-ranking: partner key', tone: 'success' };
    if (row.allowFallback) return { label: 'Re-ranking: platform', tone: 'info' };
    return { label: 'Re-ranking off', tone: 'neutral' };
  }
  if (row?.hasKey) return { label: 'Partner key', tone: 'success' };
  if (row?.allowFallback) return { label: 'Platform key', tone: 'info' };
  return { label: 'Blocked', tone: 'danger' };
};

/**
 * A partner's client company (Partner tier guide §5.5), first match wins:
 *   C.has_key                                   → Own key
 *   C exists and C.inherit_from_partner false   → Blocked
 *   P.has_key                                   → Uses partner key
 *   P.allow_platform_fallback                   → Uses platform key
 *   otherwise                                   → Blocked
 * `companyRow` is the company's matrix row (from buildKeyRows); `partnerCred` is
 * the partner's raw credential row for the same provider (may be null).
 */
export const resolvePartnerChain = (companyRow, partnerCred, { partnerName, companyName } = {}) => {
  const cohere = !!companyRow?.isCohere;
  const blocked = (tooltip) =>
    cohere
      ? { label: 'Re-ranking off', tone: 'neutral', tooltip }
      : { label: KEYS_COPY.badgeBlocked, tone: 'danger', tooltip };

  if (companyRow?.hasKey) {
    return {
      label: cohere ? 'Re-ranking: own key' : KEYS_COPY.badgeOwn(companyRow.keyLast4),
      tone: 'success',
      tooltip: KEYS_COPY.tipOwn(companyName),
    };
  }
  if (companyRow?.hasRow && !companyRow.inheritFromPartner) {
    return blocked(KEYS_COPY.tipInheritOff(companyName));
  }
  if (partnerCred?.has_key) {
    return {
      label: cohere ? 'Re-ranking: partner key' : KEYS_COPY.badgePartner,
      tone: 'info',
      tooltip: KEYS_COPY.tipPartner(partnerName, companyName),
    };
  }
  if (partnerCred?.allow_platform_fallback) {
    return {
      label: cohere ? 'Re-ranking: platform' : KEYS_COPY.badgePlatform,
      tone: 'info',
      tooltip: KEYS_COPY.tipPlatform(partnerName, companyName),
    };
  }
  return blocked(KEYS_COPY.tipBlocked);
};

/**
 * What a client company's OWN admin sees for a provider without an own key
 * (§5.5): they can't see whether the partner has a key, only the inheritance
 * flag. Returns null when the company has its own key (normal badge applies).
 */
export const resolveInheritedForCompanyAdmin = (cred, partnerName) => {
  if (cred?.has_key) return null;
  if (cred && cred.inherit_from_partner === false) {
    return {
      label: KEYS_COPY.turnedOffByPartner(partnerName),
      tone: 'danger',
      tooltip: KEYS_COPY.turnedOffByPartnerTooltip,
    };
  }
  return {
    label: KEYS_COPY.fromPartner(partnerName),
    tone: 'info',
    tooltip: KEYS_COPY.fromPartnerTooltip,
  };
};

/** True when a badge means "this provider can't be used". */
export const isBlockedTone = (tone) => tone === 'blocked' || tone === 'danger';
